import { generateId } from "@/shared/helpers/generated-id.helper";

import { type UserRolesEnum } from "../store/api/auth/auth.enum";

import {
  type IMeta,
  type INavigationCreate,
  type INavigationItem,
  type INavigationItemCreate,
  type INavigationItemWithChildren,
  type INavigationKey,
  type ISidebarItem,
} from "./interface";

export class NavigationMatcher {
  private static instance?: NavigationMatcher;

  private routes: Map<string, INavigationItem>;

  private routerPathname: string | undefined;

  private defaultRoles: UserRolesEnum[];

  private basepath: string;

  private userRoles: UserRolesEnum[] = [];

  private constructor({
    basepath,
    defaultRoles,
    children = [],
  }: INavigationCreate) {
    this.basepath = basepath;
    this.defaultRoles = defaultRoles;
    this.routes = this.generateRoutesMap(children);
  }

  public static getInstance(config?: INavigationCreate): NavigationMatcher {
    if (!NavigationMatcher.instance) {
      if (!config) {
        throw new Error(
          "NavigationMatcher is not initialized. Provide a configuration object.",
        );
      }
      NavigationMatcher.instance = new NavigationMatcher(config);
    }
    return NavigationMatcher.instance;
  }

  public static resetInstance(): void {
    NavigationMatcher.instance = undefined;
  }

  private generateRoutesMap(
    routes: INavigationItemCreate[],
  ): Map<string, INavigationItem> {
    const map = new Map<string, INavigationItem>();
    routes.forEach((route) => {
      this.addRouteToMap(route, map);
    });
    return map;
  }

  private addRouteToMap(
    route: INavigationItemCreate,
    map: Map<string, INavigationItem>,
  ) {
    const id = generateId(route.key);
    const meta: IMeta = {
      roles: route.meta?.roles || [...this.defaultRoles],
      img: route.meta?.img,
      icon: route.meta?.icon,
    };

    map.set(route.key, {
      ...route,
      id,
      meta,
      children: route.children?.map((child) => child.key) || [],
      isActive: false,
      isActiveParent: false,
    });

    route.children?.forEach((child) => this.addRouteToMap(child, map));
  }

  public push(route: INavigationItemCreate) {
    this.addRouteToMap(route, this.routes);
  }

  public getAllRoutes(): INavigationItem[] {
    return Array.from(this.routes.values());
  }

  public setUserRoles(userRoles: UserRolesEnum[]) {
    this.userRoles = userRoles;
  }

  public hasAccessToRoute(key: string): boolean {
    const route = this.findRouteByKey(key);
    if (!route) return false;

    // TODO Temporary
    const routeRoles = route.meta?.roles || this.defaultRoles;
    return (
      routeRoles.some((role) => this.userRoles.includes(role)) ||
      routeRoles.length < 1
    );
  }

  public findRoutesByKeys(keys: string[]): INavigationItemWithChildren[] {
    return keys
      .map((key) => this.findRouteByKey(key))
      .filter(
        (route): route is INavigationItemWithChildren =>
          !!route && this.hasAccessToRoute(route.key),
      );
  }

  public getAccessibleRoutes(): INavigationItem[] {
    return Array.from(this.routes.values()).filter((route) =>
      this.hasAccessToRoute(route.key),
    );
  }

  public findCurrentRoute(): INavigationItemWithChildren | undefined {
    if (!this.routerPathname) {
      throw new Error("Router is not set up. Call adjustPathname() first.");
    }

    const currentRoute = this.findRouteByHref(this.routerPathname);

    if (currentRoute && this.hasAccessToRoute(currentRoute.key)) {
      return currentRoute;
    }

    return undefined;
  }

  public findRouteByKey(key: string): INavigationItemWithChildren | undefined {
    const route = this.routes.get(key);
    if (!route) return undefined;

    const children = route.children?.length
      ? this.findRoutesByKeys(route.children)
      : [];

    return { ...route, children };
  }

  public findRouteByHref(
    href: string,
  ): INavigationItemWithChildren | undefined {
    const normalizedHref = this.normalizeHref(href);

    const route = Array.from(this.routes.values()).find((r) => {
      const routeHref = this.normalizeHref(
        NavigationMatcher.replaceRouteParams(r.href),
      );
      const regex = NavigationMatcher.routeHrefToRegexPattern(routeHref);
      return regex.test(normalizedHref);
    });
    if (!route) return undefined;

    const children = route.children?.length
      ? this.findRoutesByKeys(route.children)
      : [];

    return { ...route, children };
  }

  public isActiveRoute(href: string): boolean {
    if (!this.routerPathname) {
      throw new Error("Router is not set up. Call adjustPathname() first.");
    }

    const normalizedHref = this.normalizeHref(
      NavigationMatcher.replaceRouteParams(href),
    );
    const normalizedPathname = this.normalizeHref(this.routerPathname);

    const regex = NavigationMatcher.routeHrefToRegexPattern(normalizedHref);

    return regex.test(normalizedPathname);
  }

  private updateActiveRoute(pathname: string) {
    const normalizedPathname = this.normalizeHref(pathname);

    this.routes.forEach((route, key) => {
      const routeHref = this.normalizeHref(
        NavigationMatcher.replaceRouteParams(route.href),
      );
      const regex = NavigationMatcher.routeHrefToRegexPattern(routeHref);
      const isActive = regex.test(normalizedPathname);
      let isActiveParent = false;

      if (route.children?.length) {
        const children = this.findRoutesByKeys(route.children);
        isActiveParent = children.some((child) => {
          const childHref = this.normalizeHref(
            NavigationMatcher.replaceRouteParams(child.href),
          );
          const childRegex =
            NavigationMatcher.routeHrefToRegexPattern(childHref);
          return childRegex.test(normalizedPathname);
        });
      }

      this.routes.set(key, { ...route, isActive, isActiveParent });
    });
  }

  public getRouteName(key: string): string | undefined {
    const route = this.findRouteByKey(key);
    return route?.name;
  }

  public adjustPathname(pathname: string) {
    this.routerPathname = pathname;
    this.updateActiveRoute(pathname);
  }

  public getBasepath(): string {
    return this.basepath;
  }

  public hasRoleInRoute(key: string, role: UserRolesEnum): boolean {
    const route = this.findRouteByKey(key);
    return route?.meta?.roles?.includes(role) ?? false;
  }

  public hasAnyRoleInRoute(key: string, roles: UserRolesEnum[]): boolean {
    const route = this.findRouteByKey(key);
    return roles.some((role) => route?.meta?.roles?.includes(role));
  }

  public getRouteMeta(key: string): IMeta | undefined {
    const route = this.findRouteByKey(key);
    return route?.meta;
  }

  private normalizeHref(href: string): string {
    const normalizedHref = href.startsWith(this.basepath)
      ? href.slice(this.basepath.length)
      : href;
    return normalizedHref.replace(/\/+$/, "");
  }

  private static replaceRouteParams(
    href: string,
    params?: Record<string, string>,
  ): string {
    return href.replace(/{(\w+)}/g, (_, key) => {
      const value = params?.[key];
      if (value != null && value !== "" && value !== "undefined") {
        return value;
      }
      return `{${key}}`;
    });
  }

  public getRouteHref(
    key: string,
    params?: Record<string, string>,
  ): string | undefined {
    const route = this.findRouteByKey(key);
    if (!route) return undefined;

    if (!params) {
      if (!this.routerPathname) {
        throw new Error("Router is not set up. Call adjustPathname() first.");
      }
      params = this.extractParamsFromPathname(this.routerPathname, route.href);
    }

    return (
      this.basepath + NavigationMatcher.replaceRouteParams(route.href, params)
    );
  }

  public getBreadcrumbs(): INavigationItemWithChildren[] {
    if (!this.routerPathname) {
      throw new Error("Router is not set up. Call adjustPathname() first.");
    }

    const pathname = this.normalizeHref(this.routerPathname);
    const segments = pathname.split("/").filter(Boolean);

    const initialCurrentPath = this.basepath.endsWith("/")
      ? this.basepath.slice(0, -1)
      : this.basepath;

    const { breadcrumbs } = segments.reduce(
      (acc, segment) => {
        acc.currentPath += `/${segment}`;
        const route = this.findRouteByHref(acc.currentPath);
        if (route) {
          const params = this.extractParamsFromPathname(
            acc.currentPath,
            route.href,
          );
          const routeWithParams = this.applyParamsToRouteName(route, params);

          acc.breadcrumbs.push({
            ...routeWithParams,
            href: this.getRouteHref(routeWithParams.key, params) || "",
          });
        }
        return acc;
      },
      {
        currentPath: initialCurrentPath,
        breadcrumbs: [] as INavigationItemWithChildren[],
      },
    );

    return breadcrumbs;
  }

  private applyParamsToRouteName(
    route: INavigationItemWithChildren,
    params?: Record<string, string>,
  ): INavigationItemWithChildren {
    const name = route.name.replace(
      /{(\w+)\??}/g,
      (_, key) => params?.[key] || `{${key}}`,
    );

    return {
      ...route,
      name,
      children: route.children.map((child) =>
        this.applyParamsToRouteName(child, params),
      ),
    };
  }

  private static routeHrefToRegexPattern(href: string): RegExp {
    let pattern = href.replace(/([.+?^=!:${}()|[\]\\/{}])/g, "\\$1");

    pattern = pattern.replace(
      /\\{(\w+)\\}/g,
      (_, paramName) => `(?<${paramName}>[^/]+)`,
    );

    pattern = `^${pattern}$`;

    return new RegExp(pattern);
  }

  public getNavigation(
    keys: (string | INavigationKey)[],
    maxLevel = 1,
  ): ISidebarItem[] {
    const buildNavigationItem = (
      route: INavigationItemWithChildren,
      currentLevel: number,
      includeChildren: boolean,
    ): ISidebarItem | null => {
      const href = this.getRouteHref(route.key);
      if (!href) {
        return null;
      }

      const children =
        includeChildren && currentLevel < maxLevel
          ? route.children
            ?.filter((child) => this.hasAccessToRoute(child.key))
            .map((child) =>
              buildNavigationItem(child, currentLevel + 1, includeChildren),
            )
            .filter((child): child is ISidebarItem => child !== null)
          : undefined;

      return {
        key: route.key,
        label: route.name,
        href,
        icon: route.meta?.icon || undefined,
        children: children && children.length > 0 ? children : undefined,
        isNextSection: false,
        active: Boolean(route.isActive || route.isActiveParent),
      };
    };

    return keys
      .map((navKey) => {
        let key: string;
        let includeChildren = true;

        if (typeof navKey === "string") {
          key = navKey;
        } else {
          key = navKey.key;
          includeChildren = navKey.includeChildren ?? true;
        }

        const route = this.findRouteByKey(key);
        if (route && this.hasAccessToRoute(route.key)) {
          return buildNavigationItem(route, 0, includeChildren);
        }
        return null;
      })
      .filter((item): item is ISidebarItem => item !== null);
  }

  private extractParamsFromPathname(
    pathname: string,
    routeHref: string,
  ): Record<string, string> {
    const normalizedPathname = this.normalizeHref(pathname);

    const normalizedRouteHref = this.normalizeHref(routeHref);

    const segments = normalizedRouteHref.split("/").filter(Boolean);
    const numSegments = segments.length;

    for (let i = numSegments; i > 0; i--) {
      const hrefPrefix = `/${segments.slice(0, i).join("/")}`;

      const pattern = NavigationMatcher.generatePatternFromHref(hrefPrefix);

      const regex = new RegExp(`^${pattern}$`);

      const match = normalizedPathname.match(regex);

      if (match?.groups) {
        return match.groups;
      }
    }

    return {};
  }

  private static generatePatternFromHref(href: string): string {
    const segments = href.split("/").filter(Boolean);

    let pattern = "";

    for (let i = 0; i < segments.length; i++) {
      let segment = segments[i];

      segment = segment.replace(/([.+?^=!:${}()|[\]\\/])/g, "\\$1");

      if (segment.startsWith("\\{") && segment.endsWith("\\}")) {
        const paramName = segment.slice(2, -2);
        pattern += `/(?<${paramName}>[^/]+)`;
      } else {
        pattern += `/${segment}`;
      }
    }

    return pattern;
  }
}
